import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useApiFetch } from '../hooks/useApiFetch';
import { EvalSetFilter } from './EvalSetFilter';
import {
  DEFAULT_SORT,
  type ContentKind,
  type SearchResponse,
  type SearchResult,
  type SearchSort,
} from '../types/search';
import { getSampleViewUrl, type SampleViewAnchor } from '../utils/url';
import { ErrorDisplay } from './ErrorDisplay';
import { Layout } from './Layout';
import { SearchSnippet } from './SearchSnippet';

const DEBOUNCE_MS = 300;
const LIMIT = 50;

// Mirrors meta_server filename derivation exactly: `location.split(f"{eval_set_id}/")[-1]`
// — take the segment after the LAST occurrence of `${evalSetId}/`, falling back to
// the whole location if the marker isn't present.
function deriveFilename(
  location: string | null,
  evalSetId: string | null
): string {
  if (!location || !evalSetId) return location ?? '';
  const parts = location.split(`${evalSetId}/`);
  return parts.length > 1 ? parts[parts.length - 1] : location;
}

// Map an event/message hit to the viewer's deep-link anchor (tab + search
// param). Other hit types have no addressable transcript node.
function viewAnchor(r: SearchResult): SampleViewAnchor | undefined {
  if (!r.transcript_anchor) return undefined;
  if (r.entity_type === 'event') {
    return { tab: 'transcript', param: 'event', value: r.transcript_anchor };
  }
  if (r.entity_type === 'message' || r.entity_type === 'message_pool') {
    return { tab: 'messages', param: 'message', value: r.transcript_anchor };
  }
  return undefined;
}

// Where clicking a result should take you. Sample-attached hits deep-link into
// the transcript viewer (scrolling to the matched event/message when we have an
// anchor); eval-only hits go to the eval-set page.
function resultHref(r: SearchResult): string | null {
  if (r.sample_id && r.epoch !== null && r.eval_set_id && r.location) {
    return getSampleViewUrl({
      evalSetId: r.eval_set_id,
      filename: deriveFilename(r.location, r.eval_set_id),
      sampleId: r.sample_id,
      epoch: r.epoch,
      anchor: viewAnchor(r),
    });
  }
  if (r.eval_set_id) {
    return `/eval-set/${encodeURIComponent(r.eval_set_id)}`;
  }
  return null;
}

const CONTENT_KIND_LABELS: Record<ContentKind, string> = {
  transcript: 'transcript',
  scanned_transcript: 'scanned transcript',
  result_value: 'scan result',
  eval_metadata: 'eval',
  sample_metadata: 'sample',
};

function Badge({ result }: { result: SearchResult }) {
  const label = CONTENT_KIND_LABELS[result.content_kind] ?? result.content_kind;
  return (
    <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-200 dark:bg-brand-700/30 dark:text-brand-200 dark:ring-brand-700">
      {result.entity_type}
      {label && label !== result.entity_type ? ` · ${label}` : ''}
    </span>
  );
}

interface TranscriptGroup {
  key: string;
  evalSetId: string | null;
  taskName: string | null;
  model: string | null;
  sampleId: string | null;
  hits: SearchResult[];
}

function groupByTranscript(items: SearchResult[]): TranscriptGroup[] {
  const groups = new Map<string, TranscriptGroup>();
  for (const item of items) {
    const key = item.sample_pk ?? `eval:${item.eval_id ?? item.entity_pk}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        evalSetId: item.eval_set_id,
        taskName: item.task_name,
        model: item.model,
        sampleId: item.sample_id,
        hits: [],
      };
      groups.set(key, group);
    }
    group.hits.push(item);
  }
  return [...groups.values()];
}

// State that mirrors a URL param: locally editable, but re-synced when the param
// changes externally (back/forward nav). Adjusting state during render is
// React's "you might not need an effect" pattern — returning the same `last`
// value bails the update, so it can't loop with the debounce effect that writes
// the param.
function useUrlSyncedState(value: string) {
  const [state, setState] = useState(value);
  const [last, setLast] = useState(value);
  if (value !== last) {
    setLast(value);
    setState(value);
  }
  return [state, setState] as const;
}

export function TranscriptSearchList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiFetch } = useApiFetch();

  // Both follow their URL param on external nav (back/forward) while staying
  // locally editable — see useUrlSyncedState.
  const [query, setQuery] = useUrlSyncedState(searchParams.get('q') ?? '');
  const [evalSetId, setEvalSetId] = useUrlSyncedState(
    searchParams.get('eval_set') ?? ''
  );
  // Sort defaults to rank (relevance). The string round-trip through useUrlSyncedState
  // is fine — only "recency" needs a URL entry, anything else collapses to default.
  const [sortRaw, setSortRaw] = useUrlSyncedState(
    searchParams.get('sort') ?? DEFAULT_SORT
  );
  const sort: SearchSort = sortRaw === 'recency' ? 'recency' : DEFAULT_SORT;

  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searched, setSearched] = useState(false);

  // Keep the latest in-flight request id so stale responses are ignored.
  const requestId = useRef(0);

  const runSearch = useCallback(
    async (q: string, evalSet: string, sortMode: SearchSort) => {
      const trimmed = q.trim();
      if (!trimmed) {
        // Bump the id so any in-flight request's response is treated as stale
        // and can't repopulate results after the query was cleared. Clear
        // loading/error too: the in-flight request's `finally` now sees a stale
        // id and won't reset them, which would otherwise leave "Searching…"
        // stuck or a previous error visible.
        requestId.current++;
        setResults([]);
        setSearched(false);
        setLoading(false);
        setError(null);
        return;
      }
      const id = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          q: trimmed,
          limit: String(LIMIT),
        });
        if (evalSet) params.set('eval_set_id', evalSet);
        if (sortMode !== DEFAULT_SORT) params.set('sort', sortMode);
        // returnNonOkResponse so a 4xx/5xx comes back as a response (and we can
        // surface it) instead of useApiFetch swallowing it to null.
        const res = await apiFetch(
          `/meta/search?${params.toString()}`,
          undefined,
          {
            returnNonOkResponse: true,
          }
        );
        if (id !== requestId.current) return; // superseded
        if (!res) {
          setError(new Error('Search request failed'));
          return;
        }
        if (!res.ok) {
          // Surface the API's `detail` when present — it carries the actionable
          // hint for the 400 "too broad" and 504 "took too long" responses.
          // Fall back to the status line if the body isn't the expected shape.
          let message = `Search failed: ${res.status} ${res.statusText}`;
          try {
            const body: unknown = await res.json();
            const detail =
              body && typeof body === 'object' && 'detail' in body
                ? (body as { detail?: unknown }).detail
                : undefined;
            if (typeof detail === 'string' && detail) message = detail;
          } catch {
            // Non-JSON body — keep the status-line fallback.
          }
          setError(new Error(message));
          return;
        }
        const data = (await res.json()) as SearchResponse;
        if (id !== requestId.current) return;
        setResults(data.items);
        setSearched(true);
      } catch (err) {
        if (id === requestId.current) setError(err as Error);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [apiFetch]
  );

  // Debounce query/scope/sort -> URL + search.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const trimmed = query.trim();
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      const trimmedEvalSet = evalSetId.trim();
      if (trimmedEvalSet) next.set('eval_set', trimmedEvalSet);
      else next.delete('eval_set');
      if (sort !== DEFAULT_SORT) next.set('sort', sort);
      else next.delete('sort');
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
      void runSearch(query, trimmedEvalSet, sort);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, evalSetId, sort]);

  const groups = useMemo(() => groupByTranscript(results), [results]);

  return (
    <Layout>
      <div className="flex h-full flex-col">
        <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900">
          <h1 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
            Transcript Search
          </h1>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search transcripts — e.g. you're absolutely right, kubernetes error"
            className="w-full max-w-2xl rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:placeholder-gray-500"
          />
          <p className="mt-2 max-w-2xl text-xs text-gray-400 dark:text-gray-500">
            <code className="text-gray-500 dark:text-gray-400">
              {'"quoted"'}
            </code>{' '}
            matches those words in order (punctuation is ignored) ·{' '}
            <code className="text-gray-500 dark:text-gray-400">OR</code> for
            either term ·{' '}
            <code className="text-gray-500 dark:text-gray-400">-word</code> to
            exclude
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span>Eval set:</span>
              <EvalSetFilter value={evalSetId} onChange={setEvalSetId} />
            </div>
            <div
              role="group"
              aria-label="Sort order"
              className="flex items-center gap-2"
            >
              <span>Sort:</span>
              <SortToggle value={sort} onChange={setSortRaw} />
            </div>
          </div>
          <div className="mt-1 h-4 text-xs text-gray-500 dark:text-gray-400">
            {loading
              ? 'Searching…'
              : searched
                ? `${results.length} match${results.length === 1 ? '' : 'es'}` +
                  (results.length === LIMIT ? ` (showing first ${LIMIT})` : '')
                : ''}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {error ? (
            <ErrorDisplay message={error.message} />
          ) : searched && results.length === 0 && !loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {evalSetId
                ? `No matches in eval set ${evalSetId}.`
                : 'No matches found.'}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {groups.map(group => (
                <TranscriptGroupCard key={group.key} group={group} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function TranscriptGroupCard({ group }: { group: TranscriptGroup }) {
  const header = [group.taskName, group.model, group.evalSetId]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="border-b border-gray-100 px-4 py-2 text-sm font-medium text-gray-800 dark:border-gray-800 dark:text-gray-200">
        {header || 'Result'}
        {group.sampleId ? (
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            sample {group.sampleId}
          </span>
        ) : null}
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {group.hits.map(hit => {
          const href = resultHref(hit);
          const body = (
            <div className="flex flex-col gap-1 px-4 py-3">
              <Badge result={hit} />
              <SearchSnippet snippet={hit.snippet} />
            </div>
          );
          return (
            <li key={`${hit.entity_type}:${hit.entity_pk}`}>
              {href ? (
                // Plain <a> (full reload) so the log-viewer's separate
                // stylesheet bundle loads cleanly — matches cross-viewer links.
                <a
                  href={href}
                  className="block hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  {body}
                </a>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface SortToggleProps {
  value: SearchSort;
  onChange: (next: SearchSort) => void;
}

/**
 * Two-button toggle for sort order. Relevance is the default; switch to
 * Newest for a strict newest-first view of the same matches.
 */
function SortToggle({ value, onChange }: SortToggleProps) {
  const options: { key: SearchSort; label: string; title: string }[] = [
    {
      key: 'rank',
      label: 'Relevance',
      title: 'Sort by relevance, newest first among equals',
    },
    { key: 'recency', label: 'Newest', title: 'Sort by newest first' },
  ];
  return (
    <div className="inline-flex rounded-md border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-950">
      {options.map((opt, i) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            title={opt.title}
            onClick={() => onChange(opt.key)}
            className={[
              'px-2 py-1 text-xs',
              i > 0 ? 'border-l border-gray-300 dark:border-gray-700' : '',
              active
                ? 'bg-brand-50 text-brand-700 font-medium dark:bg-brand-700/30 dark:text-brand-200'
                : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/50',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
